import React from 'react';
import { Controller, useForm } from 'react-hook-form';
import { Modal, ModalBody, ModalHeader, Col, Row, ModalFooter, Form, Label } from 'reactstrap';
import { AgentDealStatus } from '@repo/common-types';
import Button, { ButtonSizes, ButtonVariants } from '@/components/Button/Button';
import FormInput from '@/components/forms/FormInput';
import { AgentsFilterLocal } from '@/pages/agents/Agents/agents-query-reducer';

interface AgentsFilterFormProps {
  onClose: () => void;
  onApplyFilters: (filters: AgentsFilterLocal) => void;
  currentFilters?: AgentsFilterLocal;
}

/**
 * Filter modal for Agents Management page
 * Allows filtering by deal type, user agreement type, regional manager status, and deal status
 */
const AgentsFilterForm: React.FC<AgentsFilterFormProps> = ({ onClose, onApplyFilters, currentFilters }) => {
  const { control, handleSubmit } = useForm({
    defaultValues: {
      dealType: currentFilters?.dealType,
      userAgreementType: currentFilters?.userAgreementType,
      isRegionalManager: currentFilters?.isRegionalManager,
      dealStatus: currentFilters?.dealStatus ?? [AgentDealStatus.ACTIVE, AgentDealStatus.UPCOMING],
    },
  });

  const onSubmit = (data: any) => {
    // Convert string booleans to actual booleans
    const normalizedData = {
      ...data,
      isRegionalManager: data.isRegionalManager === '' ? '' : data.isRegionalManager === 'true',
    };

    // Clean up empty values
    const cleanedFilters = Object.entries(normalizedData).reduce((acc, [key, value]) => {
      if (value === undefined) return acc;
      if (key === 'dealStatus') {
        if (Array.isArray(value) && value.length === 0) return acc;
        acc[key as keyof AgentsFilterLocal] = value as any;
        return acc;
      }
      if (value !== '') {
        acc[key as keyof AgentsFilterLocal] = value as any;
      }
      return acc;
    }, {} as AgentsFilterLocal);

    onApplyFilters(cleanedFilters);
  };

  return (
    <Modal centered isOpen>
      <Form onSubmit={handleSubmit(onSubmit)}>
        <ModalHeader toggle={onClose} className="d-flex justify-content-between align-items-center w-full">
          Agent Filters
        </ModalHeader>
        <ModalBody>
          <Row className="g-3">
            <Col xs={12}>
              <FormInput
                label="Deal Type"
                name="dealType"
                control={control}
                type="select"
                options={[
                  { label: 'All Deal Types', value: '' },
                  { label: 'Override', value: 'Override' },
                  { label: 'Flat', value: 'Flat' },
                  { label: 'No Deal', value: 'No Deal' },
                ]}
              />
            </Col>
            <Col xs={12}>
              <FormInput
                label="User Agreement Type"
                name="userAgreementType"
                control={control}
                type="select"
                options={[
                  { label: 'All Agreement Types', value: '' },
                  { label: 'Referral V2', value: 'referral_v2' },
                  { label: 'Pre-Launch Agent', value: 'pre_launch_agent' },
                  { label: 'Agent Deal V3', value: 'agent_deal_v3' },
                ]}
              />
            </Col>
            <Col xs={12}>
              <FormInput
                label="Regional Manager"
                name="isRegionalManager"
                control={control}
                type="select"
                options={[
                  { label: 'All Agents', value: '' },
                  { label: 'Regional Managers Only', value: 'true' },
                  { label: 'Non-Regional Managers', value: 'false' },
                ]}
              />
            </Col>
            <Col xs={12}>
              <Label className="form-label">Deal Status</Label>
              <Controller
                name="dealStatus"
                control={control}
                render={({ field }) => (
                  <div className="d-flex flex-column gap-2">
                    {[
                      { label: 'Active', value: AgentDealStatus.ACTIVE },
                      { label: 'Upcoming', value: AgentDealStatus.UPCOMING },
                      { label: 'Inactive', value: AgentDealStatus.INACTIVE },
                    ].map((status) => {
                      const currentValue = (field.value as AgentDealStatus[]) ?? [];
                      const isChecked = currentValue.includes(status.value);

                      const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
                        if (e.target.checked) {
                          field.onChange([...currentValue, status.value]);
                        } else {
                          field.onChange(currentValue.filter((v) => v !== status.value));
                        }
                      };

                      return (
                        <div className="form-check" key={status.value}>
                          <input
                            className="form-check-input"
                            type="checkbox"
                            value={status.value}
                            id={`deal-status-${status.value}`}
                            checked={isChecked}
                            onChange={handleChange}
                          />
                          <Label className="form-check-label" htmlFor={`deal-status-${status.value}`}>
                            {status.label}
                          </Label>
                        </div>
                      );
                    })}
                  </div>
                )}
              />
            </Col>
          </Row>
        </ModalBody>
        <ModalFooter>
          <Button
            className="btn btn-outline"
            variant={ButtonVariants.secondary}
            size={ButtonSizes.sm}
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            className="btn btn-primary"
            variant={ButtonVariants.primary}
            size={ButtonSizes.sm}
            onClick={handleSubmit(onSubmit)}
          >
            Apply Filters
          </Button>
        </ModalFooter>
      </Form>
    </Modal>
  );
};

export default AgentsFilterForm;
